import jsonpointer from "json-pointer"
import _ from "lodash"
import React, {createContext, useRef, useState} from "react"
import {defaultFor} from "../../anwendungskonfigurator/misc/default-registry"

const setValueByJsonPointer = (obj, pointer, value) => {
    if (!_.isObject(obj) || !_.isString(pointer)) {
        return obj
    }

    const pathSegments = pointer.startsWith("/")
        ? pointer.substring(1).split("/")
        : pointer.split("/")

    if (_.isEmpty(pathSegments)) {
        return value
    }

    let current = obj
    for (let i = 0; i < pathSegments.length - 1; i++) {
        const segment = pathSegments[i]
        const child = _.get(current, segment)
        if (!_.has(current, segment) || child === null || child === undefined) {
            const nextSegment = pathSegments[i + 1]
            if (!isNaN(parseInt(nextSegment, 10))) {
                _.set(current, segment, [])
            } else {
                _.set(current, segment, {})
            }
        }
        current = _.get(current, segment)
        if (!_.isObject(current)) {
            return obj
        }
    }

    const lastSegment = pathSegments[pathSegments.length - 1]
    _.set(current, lastSegment, value)

    return obj
}

export const DataContext = createContext({
    getDataFromPointer: () => {
    },
    updateData: () => {
    },
    setToDefault: () => {
    },
})

const DataContextProvider = ({children}) => {
    const [data, setData] = useState({
        notifications: [],
        suche: {},
        detail: {
            av: {
                ist: 270,
                luecke: 730,
                aufwand: 550,
                art: "ALTER",
                graph: {
                    valPrimary: 270,
                    valSecondary: 730,
                    hex: "#B3261E",
                },
            },
            au: {
                ist: 320,
                luecke: 680,
                aufwand: 340,
                art: "INVALIDITAETVOLLEEM",
                graph: {
                    valPrimary: 320,
                    valSecondary: 680,
                    hex: "#66A61E",
                },
            },
            hi: {
                ist: 840,
                luecke: 160,
                aufwand: 680,
                art: "HINTERBLIEBENEN",
                graph: {
                    valPrimary: 840,
                    valSecondary: 160,
                    hex: "#66A61E",
                },
            },
            unfall: {
                ist: 130,
                luecke: 870,
                aufwand: 740,
                art: "UNFALL",
                graph: {
                    valPrimary: 130,
                    valSecondary: 870,
                    hex: "#B3261E",
                },
            },
            hausrat: {
                ist: 80,
                luecke: 920,
                aufwand: 840,
                art: "HAUSRAT",
                graph: {
                    valPrimary: 80,
                    valSecondary: 920,
                    hex: "#B3261E",
                },
            },
            haftpflicht: {
                ist: 840,
                luecke: 160,
                aufwand: 680,
                art: "HAFTPFLICHT",
                graph: {
                    valPrimary: 840,
                    valSecondary: 160,
                    hex: "#66A61E",
                },
            },
        },
        appdata: {
            spartenauswahl: "SACH",
        },
        pageState: {
            neuerSchutz: false,
        },
        appstate: {
            activeTab: "Kunde",
            kundendaten: {
                activeView: "INIT",
            },
            finoRedirected: "false",
            bestehendeVersorgungView: "INIT",
            isNotFirstPage: false,
            isNotLastPage: true,
            currentPage: 1,
            selectedAngebot: "THEMENFELDER",
            milestoneCount: "0 Meilensteine",
            addMilestoneModalOpen: false,
            avModalOpen: false,
            auModalOpen: false,
            hiModalOpen: false,
            tariferhoehungsrechner: {
                kontaktmodalOpen:
                    window._env_.TE_ENABLE_KONTAKTDATEN === "true",
            },
            schiessplatz: {
                themenfeldauswahl: [
                    "ALTER",
                    "INVALIDITAETVOLLEEM",
                    "HINTERBLIEBENEN",
                ],
                chartauswahl: "ZIEL",
                avModalOpen: false,
                auModalOpen: false,
                hiModalOpen: false,
                avFormOpen: false,
                auFormOpen: false,
                hiFormOpen: false,
                vermoegenDialogOpen: false,
                eigenheimDialogOpen: false,
                vermoegenDialogLocalState: {
                    aktuellesVermoegen: 50000,
                    laufzeit: 10,
                    zinssatz: 2,
                },
                eigenheimDialogLocalState: {
                    aktuellesVermoegen: 50000,
                    laufzeit: 10,
                    zinssatz: 2,
                },
            },
            lebensmeilensteine: {
                container: {
                    width: 0,
                    height: 0,
                },
            },
            vertriebswerk: window?._env_?.VERTRIEBSWERK ?? "true",
            thisYear: new Date().getFullYear(),
        },
        lebensmeilensteine: {
            zonen: [],
        },
        zukunftsplanung: {
            zonen: {
                dqD7xiamCYFGoXl3: [],
                Jf2ABh90M7H7ijVp: [],
            },
        },
        schiessplatz: {
            selectedThemenfelder: [
                "ALTER",
                "INVALIDITAETVOLLEEM",
                "HINTERBLIEBENEN",
                "VERMOEGEN",
                "EIGENHEIM",
                "PFLEGE",
            ],
            themenfelder: {},
            additionalValues: {
                gesamtInvest: 0,
                restVerfuegbar: 0,
                gesamtAufwand: 0,
            },
        },
        druckcenter: {
            seiten: [
                {id: "seite-1", label: "Titelblatt", active: true},
                {id: "seite-2", label: "Abschlussfolie", active: true},
                {id: "seite-3", label: "Seitenzahlen", active: true},
            ],
            content: [
                {id: "content-1", label: "Disclaimer", active: true},
                {id: "content-2", label: "Agenda", active: true},
                {id: "content-3", label: "Analyse", active: true},
                {
                    id: "content-4",
                    label: "Versorgungskonzept ohne Preise",
                    active: true,
                },
            ],
        },
        versorgungsbilanz: {
            kunde: {
                jahr: "2030",
                ziel: "1600.42",
                gesetzlicheVersorgungBrutto: "1898.00",
                gesetzlicheVersorgungNetto: "",
                betrieblicheVersorgungBruttoEuro: "0.00",
                betrieblicheVersorgungNettoEuro: "",
                privateVersorgungBruttoEuro: "0.00",
                privateVersorgungNettoEuro: "",
                summeAllerBruttoEinkuenfteEuro: "0.00",
                nettoVersorgungAusEinkuenften: "",
                neueVersorgungBrutto: "100.00",
                summeVersorgungUndEinkuenfteEuro: "1998.00",
                nettoeinkommen: "1440.42",
                luecke: "160.42",
                gesamtKapitalbedarfEuro: "252013.31",
            },
            ehepartner: {
                jahr: "2030",
                ziel: "1200.00",
                gesetzlicheVersorgungBrutto: "950.00",
                gesetzlicheVersorgungNetto: "",
                betrieblicheVersorgungBruttoEuro: "0.00",
                betrieblicheVersorgungNettoEuro: "",
                privateVersorgungBruttoEuro: "0.00",
                privateVersorgungNettoEuro: "",
                summeAllerBruttoEinkuenfteEuro: "0.00",
                nettoVersorgungAusEinkuenften: "",
                neueVersorgungBrutto: "50.00",
                summeVersorgungUndEinkuenfteEuro: "1000.00",
                nettoeinkommen: "820.00",
                luecke: "380.00",
                gesamtKapitalbedarfEuro: "125000.00",
            },
        },
        versorgungsuebersicht: null,
        mock: {
            disbaledRiesterChartData: {
                segments: [
                    {
                        label: "Grundzulage",
                        value: 25,
                        color: "#D9D9D9",
                    },
                    {
                        label: "Kinderzulage",
                        value: 25,
                        color: "#D9D9D9",
                    },
                    {
                        label: "Steuerersparnis",
                        value: 25,
                        color: "#D9D9D9",
                    },
                    {
                        label: "Nettobeitrag",
                        value: 25,
                        color: "#D9D9D9",
                    },
                ],
                centerLabel: "Riester",
                labelDisabled: true,
            },
            disbaledRiesterChartDataVerlauf: {
                datasets: [
                    {
                        label: "Grundzulage",
                        data: new Array(40).fill(1),
                        color: "#D9D9D9",
                    },
                    {
                        label: "Kinderzulage",
                        data: new Array(40).fill(1),
                        color: "#D9D9D9",
                    },
                    {
                        label: "Steuerersparnis",
                        data: new Array(40).fill(1),
                        color: "#D9D9D9",
                    },
                    {
                        label: "Nettobeitrag",
                        data: new Array(40).fill(1),
                        color: "#D9D9D9",
                    },
                ],
            },
            disbaledAvChartData: {
                segments: [
                    {
                        label: "Grundzulage",
                        value: 25,
                        color: "#D9D9D9",
                    },
                    {
                        label: "Kinderzulage",
                        value: 25,
                        color: "#D9D9D9",
                    },
                    {
                        label: "Steuerersparnis",
                        value: 25,
                        color: "#D9D9D9",
                    },
                    {
                        label: "Nettobeitrag",
                        value: 25,
                        color: "#D9D9D9",
                    },
                ],
                centerLabel: "AV Depot",
                labelDisabled: true,
            },
            disbaledAvChartDataVerlauf: {
                datasets: [
                    {
                        label: "Grundzulage",
                        data: new Array(40).fill(1),
                        color: "#D9D9D9",
                    },
                    {
                        label: "Kinderzulage",
                        data: new Array(40).fill(1),
                        color: "#D9D9D9",
                    },
                    {
                        label: "Steuerersparnis",
                        data: new Array(40).fill(1),
                        color: "#D9D9D9",
                    },
                    {
                        label: "Nettobeitrag",
                        data: new Array(40).fill(1),
                        color: "#D9D9D9",
                    },
                ],
            },
            mockChartData: {
                bottomLabels: ["2025", "2026", "2027"],
                datasets: [
                    {
                        label: "Basis",
                        data: [
                            "1400",
                            "1500",
                            "1800",
                            "1850",
                            "1900",
                            "1950",
                            "2000",
                            "2100",
                            "2200",
                            "2300",
                            "2400",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                        ],
                        backgroundColor: "#0B2447",
                    },
                    {
                        label: "Weitere Einkuenfte",
                        data: [
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                            "1000",
                        ],
                        backgroundColor: "#1F78B4",
                    },
                    {
                        label: "Luecke",
                        data: [
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "0",
                            "400",
                            "400",
                            "400",
                            "400",
                            "400",
                            "400",
                            "400",
                            "400",
                            "400",
                            "400",
                            "400",
                            "400",
                            "400",
                        ],
                        backgroundColor: "#D62828",
                    },
                ],
            },
            avChartData: {
                bottomLabels: ["Brutto/Netto", "Altersrente"],
                datasets: [
                    {
                        label: "Brutto",
                        data: [4000, 0],
                        backgroundColor: "#0b2447",
                        stack: "Stack 0",
                    },
                    {
                        label: "Altersrente",
                        data: [0, 2000],
                        backgroundColor: "#0b2447",
                        stack: "Stack 0",
                    },
                    {
                        label: "Altersrente",
                        data: [0, 500],
                        backgroundColor: "#db5e03",
                        stack: "Stack 0",
                    },
                    {
                        label: "Netto",
                        data: [3000, 0],
                        backgroundColor: "#666666",
                        stack: "Stack 1",
                    },
                ],
            },
            bruttorente: "4.000,00 €",
            nettorente: "3.000,00 €",
            vl: "1.000,00 €",
            pflegeChartData: {
                topLabels: [
                    "ambulant stationär",
                    "ambulant stationär",
                    "ambulant stationär",
                    "ambulant stationär",
                    "ambulant stationär",
                ],
                bottomLabels: [
                    "Pflegegrad 1",
                    "Pflegegrad 2",
                    "Pflegegrad 3",
                    "Pflegegrad 4",
                    "Pflegegrad 5",
                ],
                datasets: [
                    {
                        label: "ambulant: gesetzliche leistung",
                        backgroundColor: "#2e9801",
                        data: [0, 689, 1298, 1612, 1995],
                        stack: "stack 0",
                    },
                    {
                        label: "ambulant: eigenanteil",
                        backgroundColor: "#ab0b11",
                        data: [1000, 911, 1002, 1588, 2005],
                        stack: "stack 0",
                    },
                    {
                        label: "stationär: gesetzliche leistung",
                        backgroundColor: "#2e9801",
                        data: [180, 770, 1262, 1775, 2005],
                        stack: "stack 1",
                    },
                    {
                        label: "stationär: eigenanteil",
                        backgroundColor: "#ab0b11",
                        data: [1849, 1468, 1468, 1468, 1468],
                        stack: "stack 1",
                    },
                ],
            },
            zahlMock: "1.000,00 €",
            nullEuroWert: 0,
            aufwandFix: 50,
        },
        rakete: [
            {
                nutzlast: "2000",
                atombetrieben: true,
                passagiere: 6,
                reichweite: 40000,
            },
            {
                nutzlast: "2",
                atombetrieben: false,
                passagiere: 0,
                reichweite: 5000,
            },
            {
                nutzlast: "8000",
                atombetrieben: true,
                passagiere: 8,
                reichweite: 2000000,
            },
        ],
        absicherung: {
            selected: null,
            list: [
                {
                    produkt: {
                        id: "WEHYurmMOhXz95hP",
                        children: [
                            {
                                id: "ffsH73I0xBbROlbM",
                                children: [],
                                label: "Wohnsituation",
                                type: "leafNode",
                                active: true,
                                longname: "Wohnsituation",
                                connections: [
                                    {
                                        id: "OKnbXULJUfOwBN6k",
                                        connectionType: "multiconnection",
                                        treeName: "attributes",
                                        origin: {
                                            id: "OKnbXULJUfOwBN6k",
                                            pointer: "/rakete",
                                            children: [
                                                {
                                                    id: "KLpvcIetbcQyN8XA",
                                                    children: [],
                                                    label: "Nutzlast",
                                                    type: "leafNode",
                                                    active: true,
                                                    pointer:
                                                        "/rakete/[i]/nutzlast",
                                                    question: "Nutzlast",
                                                    longname: "Nutzlast",
                                                    antworttyp: "INTEGER",
                                                },
                                                {
                                                    id: "DmgWs9sB9zEL1zzr",
                                                    children: [],
                                                    label: "Atombetrieben",
                                                    type: "leafNode",
                                                    active: true,
                                                    antworttyp: "YESNO",
                                                    pointer:
                                                        "/rakete/[i]/atombetrieben",
                                                    question: "Atombetrieben?",
                                                    longname: "Atombetrieben",
                                                },
                                                {
                                                    id: "wvOIGQQgbVYhG3on",
                                                    children: [],
                                                    label: "Passagiere",
                                                    type: "leafNode",
                                                    active: true,
                                                    pointer:
                                                        "/rakete/[i]/passagiere",
                                                    question:
                                                        "Anzahl Passagiere",
                                                    longname: "Passagiere",
                                                    antworttyp: "INTEGER",
                                                },
                                                {
                                                    id: "I5tjcPtkUAvQpK7k",
                                                    children: [],
                                                    label: "Maximale Reichweite",
                                                    type: "leafNode",
                                                    active: true,
                                                    question:
                                                        "Maximale Reichweite",
                                                    longname:
                                                        "Maximale Reichweite",
                                                    pointer:
                                                        "/rakete/[i]/reichweite",
                                                    antworttyp: "INTEGER",
                                                },
                                            ],
                                            label: "Rakete",
                                            type: "innerNode",
                                            active: true,
                                            multiple: true,
                                            longname:
                                                "Was für Raketen hast du?",
                                        },
                                    },
                                ],
                                leistungsmerkmale: [
                                    {
                                        id: "DKm0ZMdehblCu7i4",
                                        treeName: "attributes",
                                        origin: {
                                            id: "DKm0ZMdehblCu7i4",
                                            children: [],
                                            label: "Elementarschäden",
                                            type: "leafNode",
                                            active: true,
                                            longname: "Elementarschäden",
                                            pointer: "",
                                            leistungsmerkmal: true,
                                        },
                                    },
                                    {
                                        id: "VPsrFyf1F0LXOrLe",
                                        treeName: "attributes",
                                        origin: {
                                            id: "VPsrFyf1F0LXOrLe",
                                            children: [],
                                            label: "Allgefahrendeckung",
                                            type: "leafNode",
                                            active: true,
                                            longname: "Allgefahrendeckung",
                                            pointer: "",
                                            leistungsmerkmal: true,
                                        },
                                    },
                                    {
                                        id: "R21IVCDfdOdPAGrA",
                                        treeName: "attributes",
                                        origin: {
                                            id: "R21IVCDfdOdPAGrA",
                                            children: [],
                                            label: "Ableitungsrohre",
                                            type: "leafNode",
                                            active: true,
                                            longname: "Ableitungsrohre",
                                            pointer: "",
                                            leistungsmerkmal: true,
                                        },
                                    },
                                ],
                            },
                        ],
                        label: "Gebäudeversicherung",
                        type: "innerNode",
                        active: true,
                        longname: "Ihr Haus",
                        icon: "/svg/icons--haus--1765285499641-934725335.svg",
                        prio: "SEHR WICHTIG",
                    },
                    id: 1,
                    gesellschaft: "ALLIANZ",
                    status: "ABGESICHERT",
                    beitrag: 1234,
                    objekt: {
                        nutzlast: "2000",
                        atombetrieben: true,
                        passagiere: 6,
                        reichweite: 40000,
                    },
                },
            ],
        },
        taa: {
            angebotsdaten: {
                angebotsnummer: Math.random() * 1000000,
            },
        },
        tariferhoehungsrechner: {
            kontaktdaten: {
                vorname: "",
                nachname: "",
                telefonnummer: "",
                mail: "",
            },
        },
    })
    
    const dataRef = useRef(data)

    const commitData = (updater) => {
        const next = typeof updater === "function" ? updater(dataRef.current) : updater
        dataRef.current = next
        setData(next)
    }

    const updateData = (newValue, pointer) => {
        commitData((current) => ({...setValueByJsonPointer(current, pointer, newValue)}))
    }

    const getDataFromPointer = (pointer) => {
        try {
            const current = dataRef.current
            return !_.isEmpty(current) ? jsonpointer.get(current, pointer) : null
        } catch (err) {
            return null
        }
    }

    const setToDefault = (pointer) => {
        // Ein literales [i] bezeichnet keine Stelle in den Daten, sondern eine Vorlage -
        // dorthin zu schreiben ergibt keinen Sinn. Indizierte Pointer (/kinder/2/...) schon.
        if (String(pointer ?? "").includes("[i]")) return
        commitData((current) => ({
            ...setValueByJsonPointer(current, pointer, defaultFor(pointer) ?? null),
        }))
    }

    const value = {
        data,
        setData: commitData,
        updateData,
        setToDefault,
        getDataFromPointer,
    }

    return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}

export default DataContextProvider
